/**
 * Touch Panel deployment limits (Phase 5.1).
 * Production-aligned with ESP32 firmware (SB_TOUCH_PANEL_MAX_PAYLOAD_BYTES)
 * and algo-bridge-backend touch_panel_constants — not provisional.
 */

import panelIcons from '../../data/panelIcons.json';

export const TOUCH_PANEL_PAYLOAD_LIMITS = Object.freeze({
  /** Supported deployable schema versions */
  SUPPORTED_SCHEMA_VERSIONS: Object.freeze([1]),

  /** Editor/grid bounds (align with designer UI) */
  MIN_ROWS: 3,
  MAX_ROWS: 12,
  MIN_COLUMNS: 3,
  MAX_COLUMNS: 8,

  MAX_SCREENS: 20,
  MAX_BUTTONS_PER_SCREEN: 96,
  MAX_BUTTONS_TOTAL: 400,

  /**
   * Production JSON UTF-8 byte budget for MQTT/device storage.
   * Aligned with ESP32 + backend (64 KB). Single-message MQTT remains the design.
   */
  MAX_PAYLOAD_BYTES: 64 * 1024,

  /** Fallback when agent heartbeat has not reported display dimensions yet (4" square). */
  DEFAULT_DISPLAY: Object.freeze({
    width: 720,
    height: 720,
    orientation: 'landscape',
  }),

  /**
   * Known Waveshare ESP32-P4 panel profiles (aligned with signalbridge-panel sb_board).
   */
  PANEL_DISPLAY_PROFILES: Object.freeze({
    'ESP32-P4-WIFI6-Touch-LCD-7B': Object.freeze({
      width: 1024,
      height: 600,
      orientation: 'landscape',
    }),
    'ESP32-P4-WIFI6-Touch-LCD-4B': Object.freeze({
      width: 720,
      height: 720,
      orientation: 'landscape',
    }),
    'ESP32-P4-WIFI6-Touch-LCD-4.3': Object.freeze({
      width: 480,
      height: 800,
      orientation: 'portrait',
    }),
    waveshare_7inch: Object.freeze({
      width: 1024,
      height: 600,
      orientation: 'landscape',
    }),
    waveshare_4inch: Object.freeze({
      width: 720,
      height: 720,
      orientation: 'landscape',
    }),
    waveshare_43inch: Object.freeze({
      width: 480,
      height: 800,
      orientation: 'portrait',
    }),
  }),

  MIN_DISPLAY_WIDTH: 240,
  MAX_DISPLAY_WIDTH: 1280,
  MIN_DISPLAY_HEIGHT: 240,
  MAX_DISPLAY_HEIGHT: 1920,

  /** Firmware truncates button labels beyond this length (Phase 6). */
  MAX_BUTTON_LABEL_CHARS: 48,
});

/**
 * Curated panel PNG iconKey set (SPIFFS /panel-icons catalogue).
 * Unknown keys keep deploying as-is; panel falls back to an LVGL symbol.
 * Warn in designer — do not block save.
 */
export const FIRMWARE_SUPPORTED_ICON_KEYS = Object.freeze(
  panelIcons.map((it) => it.id)
);

export function isFirmwareSupportedIconKey(iconKey) {
  if (iconKey == null || iconKey === '') return true;
  return FIRMWARE_SUPPORTED_ICON_KEYS.includes(String(iconKey));
}

export const TOUCH_PANEL_CAPABILITY_KEY = 'touchPanel';
export const TOUCH_PANEL_SCHEMA_VERSIONS_KEY = 'touchPanelSchemaVersions';

/**
 * Resolve native display size from board model / profile strings.
 * @returns {{ width: number, height: number, orientation: string } | null}
 */
export function resolvePanelDisplayFromModel(modelOrProfile) {
  if (modelOrProfile == null || modelOrProfile === '') return null;

  const key = String(modelOrProfile).trim();
  const profiles = TOUCH_PANEL_PAYLOAD_LIMITS.PANEL_DISPLAY_PROFILES;
  if (profiles[key]) return profiles[key];

  const lower = key.toLowerCase();
  if (
    lower.includes('7b') ||
    lower.includes('7inch') ||
    lower.includes('7-inch') ||
    lower.includes('1024x600') ||
    lower.includes('1024×600')
  ) {
    return profiles['ESP32-P4-WIFI6-Touch-LCD-7B'];
  }
  if (
    lower.includes('4.3') ||
    lower.includes('43inch') ||
    lower.includes('480x800') ||
    lower.includes('480×800')
  ) {
    return profiles['ESP32-P4-WIFI6-Touch-LCD-4.3'];
  }
  if (
    lower.includes('800x800') ||
    lower.includes('800×800') ||
    lower.includes('3.4')
  ) {
    return { width: 800, height: 800, orientation: 'landscape' };
  }
  if (
    lower.includes('4b') ||
    lower.includes('4inch') ||
    lower.includes('4-inch') ||
    lower.includes('720x720') ||
    lower.includes('720×720')
  ) {
    return profiles['ESP32-P4-WIFI6-Touch-LCD-4B'];
  }
  return null;
}
